import React from "react";
import {
  BookOpen,
  Bell,
  ClipboardList,
  StickyNote,
  PlayCircle,
  HeadphonesIcon,
  Trophy,
  Home,
  BarChart3,
  ShoppingBag,
  User as UserIcon,
} from "lucide-react";

/** Visual mobile mockup that mirrors the screenshot's app preview */
export default function MobileMockup() {
  const tiles = [
    { label: "MCQ Practice", icon: BookOpen, bg: "bg-blue-50", color: "text-blue-600" },
    { label: "Mock Tests", icon: ClipboardList, bg: "bg-blue-50", color: "text-blue-600" },
    { label: "Notes", icon: StickyNote, bg: "bg-orange-50", color: "text-orange-600" },
    { label: "Videos", icon: PlayCircle, bg: "bg-purple-50", color: "text-purple-600" },
    { label: "AI Tutor", icon: HeadphonesIcon, bg: "bg-amber-50", color: "text-amber-600", badge: "24x7" },
    { label: "Grand Tests", icon: Trophy, bg: "bg-indigo-50", color: "text-indigo-700" },
  ];

  return (
    <div className="relative mx-auto" data-testid="hero-mobile-mockup">
      {/* Phone frame */}
      <div className="relative w-[280px] sm:w-[300px] h-[600px] sm:h-[620px] rounded-[42px] bg-slate-900 p-2.5 phone-glow">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-6 w-32 bg-slate-900 rounded-b-2xl z-10" />
        <div className="relative h-full w-full bg-white rounded-[34px] overflow-hidden flex flex-col">
          {/* Top status */}
          <div className="px-5 pt-3 pb-1 flex items-center justify-between text-[11px] font-semibold text-slate-700">
            <span>9:41</span>
            <div className="flex items-center gap-1 text-slate-700">
              <span className="opacity-80">●●●●</span>
              <span>📶</span>
            </div>
          </div>

          {/* Greeting */}
          <div className="px-5 pt-2 flex items-center justify-between">
            <div>
              <div className="text-[15px] font-bold text-slate-900">Hi, Future Doctor 👋</div>
              <div className="text-[11px] text-slate-500">Let&apos;s continue your preparation</div>
            </div>
            <div className="h-8 w-8 rounded-full bg-blue-50 ring-1 ring-blue-600/20 flex items-center justify-center">
              <Bell className="h-4 w-4 text-blue-600" />
            </div>
          </div>

          {/* NEET PG hero card */}
          <div className="mx-4 mt-3 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 text-white p-4 relative overflow-hidden">
            <div className="text-[10px] opacity-80 font-medium">DEAR DR PRO</div>
            <div className="text-xl font-extrabold leading-tight">NEET PG</div>
            <div className="text-[11px] opacity-90 mt-0.5">Your Success, Our Mission</div>
            <div className="absolute -right-2 -bottom-2 text-white/10 text-7xl">+</div>
          </div>

          {/* Tiles */}
          <div className="px-4 mt-3 grid grid-cols-3 gap-2.5">
            {tiles.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.label}
                  className="relative bg-white border border-slate-100 rounded-xl p-2.5 flex flex-col items-center text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                >
                  {t.badge && (
                    <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[8px] font-bold px-1 py-[1px] rounded">
                      {t.badge}
                    </span>
                  )}
                  <div className={`h-9 w-9 rounded-lg ${t.bg} flex items-center justify-center mb-1.5`}>
                    <Icon className={`h-4 w-4 ${t.color}`} />
                  </div>
                  <div className="text-[10px] font-semibold text-slate-800 leading-tight">{t.label}</div>
                </div>
              );
            })}
          </div>

          {/* Goal */}
          <div className="px-4 mt-3">
            <div className="text-[11px] font-bold text-slate-900">Today&apos;s Goal</div>
            <div className="flex items-end gap-1 mt-0.5">
              <span className="text-xl font-extrabold text-slate-900">20</span>
              <span className="text-[10px] text-slate-500 mb-1">/ 30 MCQs</span>
              <span className="ml-auto text-[11px] font-bold text-blue-700">66%</span>
            </div>
            <div className="mt-1 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" style={{ width: "66%" }} />
            </div>
          </div>

          {/* Bottom nav */}
          <div className="mt-auto border-t border-slate-100 px-3 py-2 grid grid-cols-5 text-[9px] font-semibold">
            {[
              { i: Home, l: "Home", a: true },
              { i: ClipboardList, l: "Tests" },
              { i: BarChart3, l: "Analytics" },
              { i: ShoppingBag, l: "Store" },
              { i: UserIcon, l: "Profile" },
            ].map((b) => {
              const I = b.i;
              return (
                <div key={b.l} className={`flex flex-col items-center gap-0.5 ${b.a ? "text-blue-600" : "text-slate-400"}`}>
                  <I className="h-4 w-4" />
                  <span>{b.l}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Decorative glow */}
      <div className="absolute -inset-8 -z-10 bg-blue-400/10 blur-3xl rounded-full" />
    </div>
  );
}
